import {
  HttpContextToken,
  HttpErrorResponse,
  HttpInterceptorFn,
  HttpStatusCode
} from '@angular/common/http'
import { inject } from '@angular/core'

import { catchError, throwError } from 'rxjs'

import { AuthService } from '@yoshu-lib/core/auth/auth.service'
import { ToastService } from '@yoshu-lib/core/toast/toast.service'

import { TranslateService } from '@ngx-translate/core'

import { ENVIRONMENT } from '../tokens/environment.token'

/** Set on a request to suppress the global error toast (caller handles the error itself). */
export const SKIP_ERROR_TOAST = new HttpContextToken<boolean>(() => false)

const KNOWN_TOP_LEVEL_KEYS = new Set(['detail', 'non_field_errors', 'errors'])

/**
 * An HTTP interceptor function that handles errors in HTTP requests.
 *
 * Shows toast notifications for Yoshu API errors and logs all HTTP errors.
 * 401 handling (token refresh, logout, redirect) is done by the authInterceptor.
 *
 * @param req - The outgoing HTTP request.
 * @param next - The next interceptor or backend handler in the chain.
 * @returns An observable of the HTTP event stream, with error handling applied.
 */
export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService)
  const toast = inject(ToastService)
  const translate = inject(TranslateService)
  const env = inject(ENVIRONMENT)

  const isYoshuApiRequest = req.url.startsWith(env.apiUrl)

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      // Skip 409 Conflict errors - they are handled specifically by the upload dialog
      // for duplicate file detection
      if (error.status === HttpStatusCode.Conflict) {
        return throwError(() => error)
      }

      // Caller opted out of the global toast — let them handle the error
      if (req.context.get(SKIP_ERROR_TOAST)) {
        console.error('HTTP error (silent): ', error)
        return throwError(() => error)
      }

      if (isYoshuApiRequest) {
        const messages = extractErrorMessages(error.error)

        if (messages.length > 0) {
          toast.error(messages.join('\n'))
        } else if (req.url.includes('/v1/auth/login')) {
          authService.logout().subscribe()
          toast.error(error.message)
        } else {
          const unknownErrorMessage = translate.instant('errors.unknown_error') as string
          toast.error(unknownErrorMessage)
        }
      }

      console.error('HTTP error: ', error)

      return throwError(() => error)
    })
  )
}

/**
 * Walks the common DRF / Yoshu error body shapes and returns a flat list of
 * human-readable messages. Returns `[]` when nothing extractable is found so
 * the caller can fall back to a generic error string.
 */
function extractErrorMessages(body: unknown): string[] {
  if (!body || typeof body !== 'object') return []

  const record = body as Record<string, unknown>
  const messages: string[] = []

  const detail = record['detail']
  if (typeof detail === 'string') {
    messages.push(detail)
  } else if (Array.isArray(detail)) {
    messages.push(...flattenToStrings(detail))
  }

  const nonFieldErrors = record['non_field_errors']
  if (Array.isArray(nonFieldErrors)) {
    messages.push(...flattenToStrings(nonFieldErrors))
  }

  const errors = record['errors']
  if (Array.isArray(errors)) {
    for (const entry of errors) {
      if (typeof entry === 'string') {
        messages.push(entry)
      } else if (entry && typeof entry === 'object') {
        for (const value of Object.values(entry)) {
          messages.push(...flattenToStrings(value))
        }
      }
    }
  }

  // Per-field DRF validation: only treat `{ <field>: string[] }` as errors.
  // Stricter than "any string value" so we don't leak non-error payloads (e.g.
  // the login-challenge response `{ error, session, username }`) into toasts.
  for (const [field, value] of Object.entries(record)) {
    if (KNOWN_TOP_LEVEL_KEYS.has(field)) continue
    if (!Array.isArray(value)) continue
    for (const item of value) {
      if (typeof item !== 'string') continue
      messages.push(`${field}: ${item}`)
    }
  }

  return Array.from(new Set(messages.filter((m) => m.trim().length > 0)))
}

function flattenToStrings(value: unknown): string[] {
  if (typeof value === 'string') return [value]
  if (!Array.isArray(value)) return []

  const result: string[] = []
  for (const item of value) {
    if (typeof item === 'string') {
      result.push(item)
    } else if (item && typeof item === 'object') {
      for (const nested of Object.values(item)) {
        result.push(...flattenToStrings(nested))
      }
    }
  }
  return result
}
